/**
 * Pop-up preflight for the multi-monitor cockpit.
 *
 * The cockpit needs to open MORE THAN ONE window from a single click
 * (viewer + report + worklist). Chrome's default popup policy allows exactly
 * one window per user gesture; only allow-listing the site ("Always allow
 * pop-ups and redirects") lifts that cap. A web page cannot read or change
 * that permission — the only reliable probe is to try.
 *
 * runPopupPreflight() opens two tiny throwaway windows in the SAME gesture
 * and classifies the result:
 *   - both open      -> 'allowlisted'  (cockpit fully works)
 *   - only one opens -> 'single'       (default policy; cockpit degrades to
 *                                       one window per click + guidance toast)
 *   - none open      -> 'blocked'      (pop-ups fully blocked for the site)
 *
 * MUST be called synchronously from a user gesture (click handler) — calling
 * it outside one consumes no gesture budget and always reports 'blocked'.
 *
 * Side benefit: a blocked probe makes the browser show its own blocked-pop-up
 * icon in the address bar, which is exactly the affordance our fix
 * instructions point at.
 */
/** Order matters: Edge and Opera UAs also contain "Chrome/". */
export function detectBrowser(ua = navigator.userAgent) {
    if (/Edg(e|A|iOS)?\//.test(ua))
        return 'edge';
    if (/Firefox\//.test(ua))
        return 'firefox';
    if (/Chrome\//.test(ua))
        return 'chrome';
    if (/Safari\//.test(ua))
        return 'safari';
    return 'other';
}
/**
 * The browser's own pop-up settings page. Web pages cannot NAVIGATE to these
 * (browsers block web->internal-page navigation), so this is surfaced as a
 * copy-to-clipboard string the user pastes into the address bar.
 */
export function popupSettingsAddress(browser) {
    switch (browser) {
        case 'chrome':
            return 'chrome://settings/content/popups';
        case 'edge':
            return 'edge://settings/content/popups';
        case 'firefox':
            return 'about:preferences#privacy';
        default:
            return null;
    }
}
// Small, top-left corner, and deliberately NOT 'noopener' — with noopener,
// window.open returns null even when the window opens, which would make a
// successful open indistinguishable from a blocked one.
const PROBE_FEATURES = 'popup=yes,width=120,height=90,left=40,top=40';
/**
 * Probe the effective pop-up policy. Call synchronously from a click handler.
 * `openWindow` is injectable for tests.
 */
export function runPopupPreflight(openWindow = (u, n, f) => window.open(u, n, f)) {
    // Distinct names — reusing one name would navigate the first window instead
    // of opening a second, making one allowed pop-up look like two.
    const first = openWindow('about:blank', 'nl-popup-probe-1', PROBE_FEATURES);
    const second = openWindow('about:blank', 'nl-popup-probe-2', PROBE_FEATURES);
    for (const w of [first, second]) {
        try {
            w?.close();
        }
        catch {
            // Cross-origin/COOP edge cases can make close() throw; the probe result
            // is already known, so a lingering blank window is cosmetic only.
        }
    }
    if (first && second)
        return 'allowlisted';
    if (first || second)
        return 'single';
    return 'blocked';
}
